import type { UseFetchOptions } from '#app'
import type { MaybeRefOrGetter } from 'vue'
import type {
  ApiResponse,
  ApiResponsePaginated,
  PaginationParams,
  UseApiFetchOptions,
} from '~/types/api.type'
import type { Client } from '~/types/client.type'
import type { MappedOmit } from '~/types/common.type'
import { defu } from 'defu'

export type ApiClientGetListParams = ApiClientGetByIdParams & PaginationParams<Client> & {
  searchTerm?: string
  noPagination?: boolean
  isActive?: boolean
}

export interface ApiClientGetByIdParams {
  withProjects?: boolean
}

export type ApiClientCreateClient = MappedOmit<Client, 'id' | 'created_at' | 'isActive'> & {
  isActive?: Client['isActive']
}

export function useApiClientGetList<T extends ApiClientGetListParams>(params: MaybeRefOrGetter<T> = {} as T, options: UseApiFetchOptions<ApiResponsePaginated<Client>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponsePaginated<Client>> = { method: 'GET', params }
  return useApiFetch('/clients', defu(notOverridableOptions, options))
}

export function useApiClientGetListUnpaginated<T extends ApiClientGetByIdParams>(params: MaybeRefOrGetter<T> = {} as T, options: UseApiFetchOptions<ApiResponse<Client[]>> = {}) {
  const paramsWithNoPagination = computed<T>(() => {
    return { ...toValue(params), noPagination: true }
  })
  const notOverridableOptions: UseFetchOptions<ApiResponse<Client[]>> = { method: 'GET', params: paramsWithNoPagination }
  return useApiFetch('/clients', defu(notOverridableOptions, options))
}

export function useApiClientGetById<T extends ApiClientGetByIdParams>(id: MaybeRefOrGetter<Client['id']>, params: MaybeRefOrGetter<T> = {} as T, options: UseFetchOptions<ApiResponse<Client>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<Client>> = { method: 'GET', params }
  return useApiFetch(() => `/clients/${toValue(id)}`, defu(notOverridableOptions, options))
}

export function useApiClientCreate(client: MaybeRefOrGetter<ApiClientCreateClient>, options: UseFetchOptions<ApiResponse<Client>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<Client>> = { method: 'POST', body: toValue(client) }
  const overridableOptions: UseFetchOptions<ApiResponse<Client>> = { immediate: false, watch: false }
  return useApiFetch('/clients', defu(notOverridableOptions, options, overridableOptions))
}

export function useApiClientUpdateById(id: MaybeRefOrGetter<Client['id']>, client: MaybeRefOrGetter<Partial<ApiClientCreateClient>>, options: UseFetchOptions<ApiResponse<Client>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<Client>> = { method: 'PATCH', body: client }
  const overridableOptions: UseFetchOptions<ApiResponse<Client>> = { immediate: false, watch: false }
  return useApiFetch(() => `/clients/${toValue(id)}`, defu(notOverridableOptions, options, overridableOptions))
}

export function useApiClientDeleteById(id: MaybeRefOrGetter<Client['id']>, options: UseFetchOptions<never> = {}) {
  const notOverridableOptions: UseFetchOptions<never> = { method: 'DELETE' }
  const overridableOptions: UseFetchOptions<never> = { immediate: false, watch: false }
  return useApiFetch(() => `/clients/${toValue(id)}`, defu(notOverridableOptions, options, overridableOptions))
}
